import { computed } from 'vue'
import { useTenant, useLocalizations, useContents, useStructures } from '@/core'
import i18n from '@/i18n'
import {
  queryContentWithLocale,
  queryStructureWithLocale,
  queryStructureChildrenWithLocale,
} from '@/graphql'
import {
  ComposeTranslations,
  gqlOptions,
} from '../../types/interface/Translations'
import { fetchSH } from '@/core/api'

const { tenant } = useTenant()
const { setObject } = useContents()
const { setStructure } = useStructures()
const { setLocalization, getLocalizationById } = useLocalizations()
const isFallbackLanguage = computed(
  () => tenant.value.langs[0].code === i18n.global.locale,
)

const postGql = (namespace, query, endpoint = 'compositions/v2') =>
  fetchSH(`${endpoint}/${namespace}/graphql`, {
    method: 'POST',
    body: JSON.stringify(query),
  })

const gqlContentQuery = async (
  contentId,
  locale = '',
  options: gqlOptions = {},
) => {
  const lang = locale || i18n.global.locale
  const namespace = options.namespace || tenant.value.namespace
  let gqlRes

  try {
    gqlRes = await postGql(namespace, {
      query: queryContentWithLocale,
      variables: {
        objectId: contentId,
        locale: lang,
      },
    })
    if (!gqlRes.data.content.data) return
    setObject([gqlRes.data.content.data])
    if (gqlRes.data.content.data.localization) {
      gqlRes.data.content.data.localization.content = {
        content: gqlRes.data.content.data.localization.content,
      }
      gqlRes.data.content.data.localization.service = 'edtech.content'
      gqlRes.data.content.data.localization.locale = lang
      gqlRes.data.content.data.localization.objectId = contentId
      setLocalization(gqlRes.data.content.data.localization)
    }
  } catch {
    //
  }

  return gqlRes
}

const gqlStructureQuery = async (id, locale = '', options: gqlOptions = {}) => {
  const lang = locale || i18n.global.locale
  const namespace = options.namespace || tenant.value.namespace
  let gqlRes

  try {
    gqlRes = await postGql(namespace, {
      query: queryStructureWithLocale,
      variables: {
        id: encodeURIComponent(id),
        locale: lang,
      },
    })
    setStructure([gqlRes.data.structure.data])
    gqlRes.data.structure.data.slugPath = id
    if (gqlRes.data.structure.data.localization.data) {
      gqlRes.data.structure.data.localization.data.objectId =
        gqlRes.data.structure.data.id
      setLocalization(gqlRes.data.structure.data.localization.data)
      gqlRes.data.structure.data.name =
        getLocalizationById(gqlRes.data.structure.data.id, locale, 'structure')
          ?.content.name || gqlRes.data.structure.data.name
      gqlRes.data.structure.data.description =
        getLocalizationById(gqlRes.data.structure.data.id, locale, 'structure')
          ?.content.description || gqlRes.data.structure.data.description
    }
  } catch {
    //
  }

  return gqlRes
}

const gqlStructureChildrenQuery = async (
  id,
  locale = '',
  options: gqlOptions = {},
) => {
  const lang = locale || i18n.global.locale
  const namespace = options.namespace || tenant.value.namespace
  let gqlRes

  try {
    gqlRes = await postGql(namespace, {
      query: queryStructureChildrenWithLocale,
      variables: {
        id: encodeURIComponent(id),
        locale: lang,
        limit: options?.limit || 10,
        page: options?.page || 0,
      },
    })

    gqlRes.data.structure.data.children.data.forEach((child) => {
      const childLocalization = child.localization.data
      delete child.localization
      setStructure([child])
      if (childLocalization) {
        childLocalization.objectId = child.id
        setLocalization(childLocalization)
        child.name = getLocalizationById(
          child.id,
          locale,
          'structure',
        )?.content.name
        child.description = getLocalizationById(
          child.id,
          locale,
          'structure',
        )?.content.description
      }
    })
  } catch {
    //
  }

  return gqlRes
}

export default (): ComposeTranslations => ({
  isFallbackLanguage,
  gqlContentQuery,
  gqlStructureQuery,
  gqlStructureChildrenQuery,
})
